import { CaretRight } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { Link } from "@tanstack/react-router";
import { Fragment, useEffect, useSyncExternalStore } from "react";
import { Breadcrumb, Breadcrumbs } from "react-aria-components";
import { cx } from "@/lib/cx";

export type Crumb = { label: string; to?: string };

let crumbs: Crumb[] = [];
const listeners = new Set<() => void>();
const set = (next: Crumb[]) => {
  crumbs = next;
  for (const l of listeners) l();
};

/**
 * Pages declare their place in the hierarchy; the canvas top bar renders it. The last crumb is the
 * current page (aria-current) and also the document title. The visible h1 is the page's PageHeader.
 */
export function useBreadcrumbs(items: Crumb[]) {
  const key = items.map((i) => `${i.label}\u0000${i.to ?? ""}`).join("\u0001");
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` captures the content of `items`
  useEffect(() => {
    set(items);
    const last = items[items.length - 1];
    document.title = last ? `${last.label} - ${m.app_name()}` : m.app_name();
    return () => set([]);
  }, [key]);
}

export function HeaderBreadcrumbs() {
  const items = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    () => crumbs,
  );
  if (items.length === 0) return <div className="min-w-0 flex-1" />;
  return (
    <Breadcrumbs className="flex min-w-0 flex-1 items-center gap-1 text-sm">
      {items.map((c, i) => {
        const last = i === items.length - 1;
        return (
          // The id follows the crumb (not its position): a crumb that moves when the trail grows (a page
          // that first shows only its own name, then the full path) must not change the id of a mounted item.
          // Phones keep the parent and the current page only: three truncated crumbs say nothing.
          <Breadcrumb
            key={c.to ?? c.label}
            id={c.to ?? c.label}
            className={cx(
              "flex min-w-0 items-center gap-1",
              i < items.length - 2 && "max-sm:hidden",
              // The parent keeps a readable share instead of collapsing to "PL...".
              !last && "max-sm:max-w-[40%] max-sm:shrink-0",
            )}
          >
            {last ? (
              // The page's h1 lives in PageHeader; the trail only marks the current location.
              <span className="truncate-title px-1 text-fg" title={c.label} aria-current="page">
                {c.label}
              </span>
            ) : (
              <Fragment>
                {c.to ? (
                  <Link
                    to={c.to}
                    className="truncate-title transition-ui rounded-control px-1 text-fg-muted hover:text-fg"
                    title={c.label}
                  >
                    {c.label}
                  </Link>
                ) : (
                  <span className="truncate-title px-1 text-fg-muted">{c.label}</span>
                )}
                <CaretRight size={12} aria-hidden="true" className="shrink-0 text-fg-subtle" />
              </Fragment>
            )}
          </Breadcrumb>
        );
      })}
    </Breadcrumbs>
  );
}
